import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Camera, Download, Images, Clock, Trash, Play, Flag } from 'lucide-react'
import { motion } from 'framer-motion'
import { useAuthStore } from '@/store'
import PhotoViewer from '@/components/PhotoViewer'
import ReportSheet from '@/components/ReportSheet'
import type { EventConfig } from '@/types'

interface PublicPhoto {
  id: string
  type: string
  url: string
  thumbnailUrl: string
  downloadUrl: string
  createdAt: string
  sessionId: string // Added to easily resolve session inside flat list
}

interface PublicSession {
  id: string
  eventId: string
  createdAt: string
  photos: PublicPhoto[]
  photoCount: number
  thumbnailUrl: string | null
  surveyResponses?: Record<string, string>
}

export default function PublicGallery() {
  const [params, setParams] = useSearchParams()
  const eventId = params.get('eventId') || ''
  const token = params.get('token')
  const targetSessionId = params.get('sessionId')
  const navigate = useNavigate()
  
  const { user, initialized: authInitialized, checkAuth } = useAuthStore()
  const [sessions, setSessions] = useState<PublicSession[]>([])
  const [event, setEvent] = useState<EventConfig | null>(null)
  const [eventName, setEventName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedSession, setSelectedSession] = useState<PublicSession | null>(null)

  // Interactive views overrides
  const [localGroupBySession, setLocalGroupBySession] = useState(true)
  const [localSortBy, setLocalSortBy] = useState<'newestFirst' | 'oldestFirst'>('newestFirst')
  const [hasInitializedView, setHasInitializedView] = useState(false)
  const [flatLightboxIndex, setFlatLightboxIndex] = useState<number | null>(null)
  const [sessionViewerIndex, setSessionViewerIndex] = useState<number | null>(null)
  const [sessionReportOpen, setSessionReportOpen] = useState(false)

  // Ordered so the print composite is always first — it's the shareable
  // keepsake, and the viewer opens on it by default. sessionId is stamped
  // onto each photo since the viewer (and photo reports) need it, but the
  // /api/sessions/:id response only carries it at the session level.
  const sessionViewerPhotos = selectedSession
    ? [...selectedSession.photos]
        .sort((a, b) => {
          if (a.type === 'layout') return -1
          if (b.type === 'layout') return 1
          return 0
        })
        .map((p) => ({ ...p, sessionId: selectedSession.id }))
    : []
  const sessionHero = sessionViewerPhotos[0] || null
  const sessionOthers = sessionViewerPhotos.slice(1)

  const openLightbox = async (sessionId: string) => {
    try {
      const res = await fetch(`/api/sessions/${sessionId}`)
      if (res.ok) {
        const fullSession = await res.json()
        setSelectedSession(fullSession)
      }
    } catch (e) {
      console.error('Failed to load session details', e)
    }
  }

  const handleDeletePhoto = async (sessionId: string, photoId: string) => {
    if (!window.confirm('Weet je zeker dat je deze foto wilt verwijderen?')) return

    try {
      const res = await fetch(`/api/sessions/${sessionId}/photos/${photoId}`, {
        method: 'DELETE'
      })

      if (res.ok) {
        // 1. Update main sessions list
        setSessions(prevSessions =>
          prevSessions.map(session => {
            if (session.id !== sessionId) return session
            const updatedPhotos = session.photos.filter(p => p.id !== photoId)
            const layoutPhoto = updatedPhotos.find(p => p.type === 'layout')
            const thumbPhoto = layoutPhoto || updatedPhotos[0]
            return {
              ...session,
              photos: updatedPhotos,
              photoCount: updatedPhotos.length,
              thumbnailUrl: thumbPhoto ? thumbPhoto.thumbnailUrl : null
            }
          // Remove sessions that now have no photos
          }).filter(session => session.photoCount > 0)
        )

        // 2. Update selected session details if open
        if (selectedSession && selectedSession.id === sessionId) {
          const updatedPhotos = selectedSession.photos.filter(p => p.id !== photoId)
          if (updatedPhotos.length === 0) {
            setSelectedSession(null)
          } else {
            setSelectedSession({
              ...selectedSession,
              photos: updatedPhotos
            })
          }
        }

        // 3. Close flat lightbox if open
        if (flatLightboxIndex !== null) {
          setFlatLightboxIndex(null)
        }
      } else {
        alert('Foto verwijderen mislukt. Controleer je beheerdersrechten.')
      }
    } catch (e) {
      console.error('Error deleting photo:', e)
      alert('Er ging iets mis bij het verwijderen van de foto.')
    }
  }

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm('Weet je zeker dat je deze hele sessie wilt verwijderen? Dit verwijdert alle foto\'s in één keer.')) return
    try {
      const res = await fetch(`/api/sessions/${sessionId}`, { method: 'DELETE' })
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.id !== sessionId))
        setSelectedSession(null)
      } else {
        alert('Sessie verwijderen mislukt. Controleer je beheerdersrechten.')
      }
    } catch (e) {
      console.error('Error deleting session:', e)
      alert('Er ging iets mis bij het verwijderen van de sessie.')
    }
  }

  useEffect(() => {
    if (!authInitialized) {
      checkAuth()
      return
    }

    const checkAccessAndLoad = async () => {
      try {
        if (!eventId) {
          if (!user) {
            navigate('/login', { replace: true })
            return
          }
          setLoading(false)
          return
        }

        const eventRes = await fetch(`/api/events/${eventId}`)
        if (!eventRes.ok) {
          navigate('/login', { replace: true })
          return
        }
        const eventData = await eventRes.json()
        const ev = eventData.config as EventConfig
        setEvent(ev)
        setEventName(ev.name)

        // Verify access control
        const isLoggedIn = !!user
        const gallerySettings = ev.gallerySettings || {
          enabled: true,
          publicAccess: 'disabled',
          publicAccessDuration: 'permanent',
          publicAccessDurationValue: 1,
          publicAccessExpiresAt: null,
          publicAccessCreatedAt: null,
          publicToken: null,
          sortBy: 'newestFirst',
          groupBySession: true
        }

        let isAccessAllowed = isLoggedIn || params.get('public') === 'true'

        if (!isAccessAllowed && gallerySettings.publicAccess === 'enabled' && gallerySettings.publicToken && token === gallerySettings.publicToken) {
          const expiresAt = gallerySettings.publicAccessExpiresAt
          if (!expiresAt) {
            isAccessAllowed = true
          } else {
            const expiryTime = new Date(expiresAt).getTime()
            if (expiryTime > Date.now()) {
              isAccessAllowed = true
            }
          }
        }

        // Always allow public access if a specific session ID is targeted (QR code scanning)
        if (!isAccessAllowed && targetSessionId) {
          isAccessAllowed = true
        }

        if (!isAccessAllowed) {
          navigate('/login', { replace: true })
          return
        }

        if (!hasInitializedView) {
          setLocalGroupBySession(gallerySettings.groupBySession !== false)
          setLocalSortBy(gallerySettings.sortBy || 'newestFirst')
          setHasInitializedView(true)
        }

        // Fetch gallery photos
        const galleryRes = await fetch(`/api/gallery?eventId=${eventId}`)
        if (galleryRes.ok) {
          const data = await galleryRes.json()
          if (Array.isArray(data)) {
            // Filter out empty sessions (e.g. all photos were reported and deleted)
            const nonEmpty = data.filter((s: PublicSession) => s.photoCount > 0)
            // If accessing via session-level bypass (no token/login), restrict view to only that session
            const showOnlyTargetSession = !isLoggedIn && (!gallerySettings.publicToken || token !== gallerySettings.publicToken)
            if (showOnlyTargetSession && targetSessionId) {
              setSessions(nonEmpty.filter((s: PublicSession) => s.id === targetSessionId))
            } else {
              setSessions(nonEmpty)
            }
            if (targetSessionId) {
              openLightbox(targetSessionId)
            }
          }
        }
        setLoading(false)
      } catch (err) {
        console.error('Gallery access validation failed:', err)
        navigate('/login', { replace: true })
      }
    }

    checkAccessAndLoad()
  }, [eventId, token, targetSessionId, authInitialized, user, checkAuth, navigate, hasInitializedView])

  const handleCloseLightbox = () => {
    setSelectedSession(null)
    setSessionViewerIndex(null)
    setSessionReportOpen(false)
    if (targetSessionId) {
      params.delete('sessionId')
      setParams(params)
    }
  }

  // Get sorted sessions list based on local preference
  const getSortedSessions = () => {
    const sorted = [...sessions]
    if (localSortBy === 'newestFirst') {
      sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    } else {
      sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    }
    return sorted
  }

  // Get flattened photos list
  const getFlatPhotos = () => {
    let allPhotos: Array<PublicPhoto & { sessionId: string; sessionCreatedAt: string }> = []
    const sortedSessions = getSortedSessions()
    for (const session of sortedSessions) {
      const sessionPhotos = session.photos || []
      for (const p of sessionPhotos) {
        allPhotos.push({
          ...p,
          sessionId: session.id,
          sessionCreatedAt: session.createdAt
        })
      }
    }
    if (localSortBy === 'newestFirst') {
      allPhotos.sort((a, b) => new Date(b.createdAt || b.sessionCreatedAt).getTime() - new Date(a.createdAt || a.sessionCreatedAt).getTime())
    } else {
      allPhotos.sort((a, b) => new Date(a.createdAt || a.sessionCreatedAt).getTime() - new Date(b.createdAt || b.sessionCreatedAt).getTime())
    }
    return allPhotos
  }

  // Get grid columns class
  const columns = event?.gallerySettings?.gridColumns || 3
  const getGridColsClass = () => {
    if (columns === 2) return "grid gap-4 sm:grid-cols-1 md:grid-cols-2"
    if (columns === 4) return "grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
    return "grid gap-4 sm:grid-cols-2 md:grid-cols-3"
  }

  const sortedSessionsList = getSortedSessions()
  const flatPhotosList = getFlatPhotos()

  return (
    <div className="min-h-screen bg-[#08080f] text-white">
      {/* Header */}
      <div className="border-b border-white/5 bg-white/[0.02] px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
              <Camera className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">
                Pose<span className="text-blue-400">Box</span> Galerij
              </h1>
              <p className="text-xs text-white/40">{eventName || "Bekijk en download je foto's"}</p>
            </div>
          </div>

          {(!!user || event?.gallerySettings?.allowBulkDownload === true) && eventId && (
            <a
              href={`/api/gallery/events/${eventId}/download`}
              className="flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors text-white shadow-lg shadow-blue-600/25"
            >
              <Download className="h-4 w-4" />
              <span>Download alle foto's</span>
            </a>
          )}
        </div>
      </div>

      {/* View Options Controls Bar */}
      {!loading && sessions.length > 0 && (
        <div className="border-b border-white/5 bg-white/[0.01] px-6 py-3">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4">
            {/* View Mode */}
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-white/40 uppercase tracking-wider">Groeperen per sessie</span>
              <div className="inline-flex rounded-xl bg-white/5 p-1 border border-white/5">
                <button
                  type="button"
                  onClick={() => setLocalGroupBySession(true)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${localGroupBySession ? 'bg-blue-600 text-white shadow-lg' : 'text-white/60 hover:text-white'}`}
                >
                  Ja
                </button>
                <button
                  type="button"
                  onClick={() => setLocalGroupBySession(false)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${!localGroupBySession ? 'bg-blue-600 text-white shadow-lg' : 'text-white/60 hover:text-white'}`}
                >
                  Nee (platte lijst)
                </button>
              </div>
            </div>

            {/* Sort Order */}
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-white/40 uppercase tracking-wider">Sorteren op</span>
              <div className="inline-flex rounded-xl bg-white/5 p-1 border border-white/5">
                <button
                  type="button"
                  onClick={() => setLocalSortBy('newestFirst')}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${localSortBy === 'newestFirst' ? 'bg-blue-600 text-white shadow-lg' : 'text-white/60 hover:text-white'}`}
                >
                  Nieuwste
                </button>
                <button
                  type="button"
                  onClick={() => setLocalSortBy('oldestFirst')}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${localSortBy === 'oldestFirst' ? 'bg-blue-600 text-white shadow-lg' : 'text-white/60 hover:text-white'}`}
                >
                  Oudste
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Grid View */}
      <div className="mx-auto max-w-6xl px-6 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-white/40">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-blue-500 mb-4" />
            Galerij laden…
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-white/5">
              <Images className="h-9 w-9 opacity-30" />
            </div>
            <p className="text-xl font-semibold text-white/60">Nog geen foto's</p>
            <p className="mt-2 text-sm text-white/30">Foto's die tijdens het evenement worden gemaakt verschijnen hier</p>
          </div>
        ) : localGroupBySession ? (
          /* Grouped View (Sessions) */
          <>
            <p className="mb-6 text-sm text-white/40">{sortedSessionsList.length} sessie{sortedSessionsList.length !== 1 ? 's' : ''} vastgelegd</p>
            <div className={getGridColsClass()}>
              {sortedSessionsList.map((session, i) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="group relative cursor-pointer overflow-hidden rounded-2xl border border-white/5 bg-white/[0.03] transition-all hover:border-white/10 hover:shadow-[0_0_30px_-8px_rgba(99,102,241,0.3)]"
                  onClick={() => openLightbox(session.id)}
                >
                  <div className="aspect-square bg-white/[0.02]">
                    {session.thumbnailUrl ? (
                      <img src={session.thumbnailUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Camera className="h-8 w-8 opacity-20" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    {event?.gallerySettings?.showCaptureTime !== false && (
                      <div className="text-xs text-white/40 flex items-center gap-1 mb-1">
                        <Clock className="h-3 w-3" />
                        {new Date(session.createdAt).toLocaleString()}
                      </div>
                    )}
                    <div className="text-xs text-white/30">{session.photoCount} foto{session.photoCount !== 1 ? "'s" : ''}</div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                    <div className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur">
                      Bekijk foto's
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        ) : (
          /* Flat View (All photos in a single page) */
          <>
            <p className="mb-6 text-sm text-white/40">{flatPhotosList.length} foto{flatPhotosList.length !== 1 ? "'s" : ''} vastgelegd</p>
            <div className={getGridColsClass()}>
              {flatPhotosList.map((photo, i) => (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="group relative cursor-pointer overflow-hidden rounded-2xl border border-white/5 bg-white/[0.03] transition-all hover:border-white/10 hover:shadow-[0_0_30px_-8px_rgba(99,102,241,0.3)]"
                  onClick={() => setFlatLightboxIndex(i)}
                >
                  <div className="aspect-square bg-white/[0.02] relative">
                    <img src={photo.thumbnailUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                    {photo.type === 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="rounded-full bg-black/60 p-3">
                          <Play className="h-5 w-5 text-white fill-white" />
                        </div>
                      </div>
                    )}
                  </div>
                  {event?.gallerySettings?.showCaptureTime !== false && (
                    <div className="p-3">
                      <div className="text-xs text-white/40 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(photo.createdAt).toLocaleString()}
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                    <div className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur">
                      Bekijk foto
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Grouped Session Lightbox */}
      {selectedSession && (
        <div
          className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/90 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={handleCloseLightbox}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            /* Full-bleed on phones — the old card border + p-6 padding ate
               roughly 15% of a small screen for pure decoration. */
            className="relative flex w-full max-w-3xl flex-col overflow-y-auto bg-[#0d0d18] p-4 shadow-2xl sm:max-h-[90vh] sm:rounded-2xl sm:border sm:border-white/10 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex shrink-0 items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-xl font-bold">Jouw foto's</h2>
                <p className="text-sm text-white/40">{new Date(selectedSession.createdAt).toLocaleString()}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {/* Report button */}
                <button
                  onClick={() => setSessionReportOpen(true)}
                  aria-label="Deze foto's melden"
                  title="Deze foto's melden"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/70 active:bg-white/25 transition-colors"
                >
                  <Flag className="h-4 w-4" />
                </button>
                {/* Admin: delete entire session at once */}
                {!!user && (
                  <button
                    onClick={() => handleDeleteSession(selectedSession.id)}
                    aria-label="Sessie verwijderen"
                    title="Hele sessie verwijderen"
                    className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-red-500/15 px-4 text-sm font-semibold text-red-400 hover:bg-red-500/30 transition-colors"
                  >
                    <Trash className="h-4 w-4" />
                    Sessie verwijderen
                  </button>
                )}
                <button
                  onClick={handleCloseLightbox}
                  aria-label="Sluiten"
                  className="flex h-10 items-center justify-center rounded-full bg-white/10 px-4 text-sm font-medium active:bg-white/25 transition-colors"
                >
                  {targetSessionId ? 'Alle foto\'s' : 'Sluiten'}
                </button>
              </div>
            </div>

            {/* The composite is the keepsake people actually post, so it gets
                hero treatment: full width, object-contain (never cropped), and
                first in the tap order. */}
            {sessionHero && (
              <div className="relative mb-3">
                <button
                  onClick={() => setSessionViewerIndex(0)}
                  className="relative block w-full overflow-hidden rounded-2xl border border-white/10 bg-black/40 active:scale-[0.99] transition-transform"
                >
                  <img
                    src={sessionHero.url}
                    alt=""
                    className="max-h-[52vh] w-full object-contain"
                  />
                  <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white/90 backdrop-blur">
                    Tik om te openen
                  </span>
                </button>
                {/* Admin: delete hero/collage photo */}
                {!!user && (
                  <button
                    onClick={() => handleDeletePhoto(selectedSession.id, sessionHero.id)}
                    aria-label="Foto verwijderen"
                    className="absolute right-2 top-2 rounded-full bg-black/70 p-2 text-red-400 backdrop-blur active:bg-red-500 active:text-white transition-colors"
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {sessionOthers.map((photo) => {
                const viewerIdx = sessionViewerPhotos.findIndex((p) => p.id === photo.id)
                return (
                  <div key={photo.id} className="relative">
                    <button
                      onClick={() => setSessionViewerIndex(viewerIdx)}
                      className="relative block w-full overflow-hidden rounded-xl active:scale-[0.98] transition-transform"
                    >
                      <img
                        src={photo.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        className="aspect-square w-full object-cover"
                      />
                      {photo.type === 'video' && (
                        <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                          <span className="rounded-full bg-black/70 p-2.5">
                            <Play className="h-4 w-4 fill-white text-white" />
                          </span>
                        </span>
                      )}
                    </button>
                    {/* Admin-only delete stays a separate, always-visible control
                        rather than a hover overlay, which never worked on touch. */}
                    {!!user && (
                      <button
                        onClick={() => handleDeletePhoto(selectedSession.id, photo.id)}
                        aria-label="Foto verwijderen"
                        className="absolute right-1.5 top-1.5 rounded-full bg-black/70 p-2 text-red-400 backdrop-blur active:bg-red-500 active:text-white transition-colors"
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Full-width and clearly labelled — previously an icon whose label
                was hidden on exactly the screens that needed it most. */}
            {event?.gallerySettings?.allowZipDownload !== false && (
              <a
                href={`/api/gallery/${selectedSession.id}/download`}
                className="mt-4 flex h-14 shrink-0 items-center justify-center gap-2 rounded-2xl bg-blue-600 text-base font-bold text-white active:scale-[0.99] active:bg-blue-500 transition-all"
              >
                <Download className="h-5 w-5" />
                Download alle {selectedSession.photos.length} foto's
              </a>
            )}

            {event?.gallerySettings?.showSurveyResults === true && selectedSession.surveyResponses && Object.keys(selectedSession.surveyResponses).length > 0 && (
              <div className="mt-6 border-t border-white/10 pt-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-white/40 mb-3">Enquête antwoorden</h3>
                <div className="grid gap-2 sm:grid-cols-2 text-sm text-white/70">
                  {Object.entries(selectedSession.surveyResponses).map(([key, val]) => (
                    <div key={key} className="rounded-lg bg-white/5 p-3">
                      <span className="text-xs text-white/40 block capitalize mb-0.5">{key}</span>
                      <span className="font-semibold text-white/90">{String(val || '(leeg)')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Deliberately a sibling of the lightbox, not a child: the lightbox card
          animates with a scale transform, and a transformed ancestor would trap
          this fixed-position sheet inside the card instead of the viewport. */}
      {selectedSession && (
        <ReportSheet
          open={sessionReportOpen}
          scope="session"
          eventId={eventId}
          sessionId={selectedSession.id}
          photoCount={selectedSession.photos.length}
          onClose={() => setSessionReportOpen(false)}
        />
      )}

      {/* Full-screen viewers — swipe, zoom, native share. Shared component so
          the session view (what the booth QR opens) and the all-photos view
          behave identically. */}
      {flatLightboxIndex !== null && flatPhotosList[flatLightboxIndex] && (
        <PhotoViewer
          photos={flatPhotosList}
          index={flatLightboxIndex}
          eventId={eventId}
          eventName={eventName}
          onIndexChange={setFlatLightboxIndex}
          onClose={() => setFlatLightboxIndex(null)}
        />
      )}

      {sessionViewerIndex !== null && sessionViewerPhotos[sessionViewerIndex] && (
        <PhotoViewer
          photos={sessionViewerPhotos}
          index={sessionViewerIndex}
          eventId={eventId}
          eventName={eventName}
          onIndexChange={setSessionViewerIndex}
          onClose={() => setSessionViewerIndex(null)}
        />
      )}
    </div>
  )
}
